import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { RippleModule } from 'primeng/ripple';
import { ButtonModule } from 'primeng/button';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { UserImageBlockComponent } from '../user-image-block/user-image-block.component';
import { StateService } from '../../../core/state/old/state.service';
import { Observable, Subject, map, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { DataService } from '../../../core/providers/data/data.service';
import { SIGN_OUT } from './sign-out.graphql';
import { Address, Customer, GetActiveCustomerQuery, GetOrderListQuery, GetOrderListQueryVariables, SignOutMutation } from '../../../graphql/generated/generated-types';
import { GET_USER_MENU_ACTIVE_CUSTOMER } from './user-menu-active-customer.graphql';
import { Platform } from '@angular/cdk/platform';
import { CommonModule, Location } from '@angular/common';
import { ActiveService } from '../../../core/providers/active/active.service';
import { NotAvailableOverlayComponent } from '../not-available-overlay/not-available-overlay.component';
import { GET_ORDER_LIST } from '../../../routes/profile/routes/orders/account-order-list.graphql';
import { GET_ORDER_COUNT } from './get-orders-count.graphql';
import { SavedItemsService } from '../../services/saved-items.service';
import { environment } from '../../../../environments/environment';
import { VehicleTypeService } from '../../services/vehicle-type.service';
import { SelectedModificationService } from '../../services/selected-modification.service';
import { reloadCurrentComponent } from '../../utils/reload-current-navigation';

export enum ProfileMenuLink {
  Orders = 'Orders',
  Account = 'Account',
  Addresses = 'Addresses',
  SavedItems = "Saved items",
  LogOut = 'Log out',
  DeleteAccount = 'Delete account'
}

export enum ProfileMenuFragment {
  Orders = 'orders',
  Account = 'account',
  Addresses = 'addresses',
  SavedItems = 'saved',
  LogOut = 'logout',
  DeleteAccount = 'delete-account'
}

export interface ProfileMenuLinkItem {
  label: ProfileMenuLink,
  fragment: ProfileMenuFragment,
  iconUrl?: string,
  activeIconUrl?: string,
  active: boolean,
  routerLink?: string,
  children?: ProfileMenuLinkItem[],
  count?: number,
  disabled?: boolean,
  command?(event: any): void;
}

@Component({
  selector: 'app-user-inner-menu',
  standalone: true,
  imports: [CommonModule, RippleModule, ButtonModule, RouterModule, UserImageBlockComponent, NotAvailableOverlayComponent],
  templateUrl: './user-inner-menu.component.html',
  styleUrl: './user-inner-menu.component.scss'
})
export class UserInnerMenuComponent implements OnInit, OnDestroy {
  @Input() stickyToTop!: boolean;
  @Input() inMobileSidenav!: boolean;



  public profileMenuList: ProfileMenuLinkItem[] = [
    {
      label: ProfileMenuLink.Orders, fragment: ProfileMenuFragment.Orders,
      iconUrl: './../../../../assets/images/user-menu/shopping_cart_black_24dp.svg',
      activeIconUrl: "./../../../../assets/images/user-menu/shopping_cart_black_24dp_active.svg",
      active: false,
      routerLink: '/user/orders',
      count: 0
    },

    {
      label: ProfileMenuLink.SavedItems, fragment: ProfileMenuFragment.SavedItems,
      iconUrl: './../../../../assets/images/user-menu/star.svg',
      activeIconUrl: "./../../../../assets/images/user-menu/star_active.svg",
      active: false,
      disabled: !environment.websiteSettings.enableSavedItems,
      routerLink: '/user/saved-items',
      count: 0
    },


    {
      label: ProfileMenuLink.Account, fragment: ProfileMenuFragment.Account,
      iconUrl: './../../../../assets/images/user-menu/account_circle_black_24dp.svg',
      activeIconUrl: "./../../../../assets/images/user-menu/account_circle_black_24dp_active.svg",
      active: false,
      routerLink: '/user/account',
      children: [
        {
          label: ProfileMenuLink.DeleteAccount, fragment: ProfileMenuFragment.DeleteAccount,
          active: false,
          routerLink: '/user/delete-account',
        }
      ]
    },
    {
      label: ProfileMenuLink.Addresses, fragment: ProfileMenuFragment.Addresses,
      iconUrl: './../../../../assets/images/user-menu/local_shipping.svg',
      activeIconUrl: "./../../../../assets/images/user-menu/local_shipping_active.svg",
      active: false,
      routerLink: '/user/addresses',
    },
    {
      label: ProfileMenuLink.LogOut, fragment: ProfileMenuFragment.LogOut,
      iconUrl: '/../../../../assets/images/user-menu/power_settings_new_black_24dp.svg',
      activeIconUrl: "./../../../../assets/images/user-menu/power_settings_new_black_24dp_active.svg",
      active: false,
      command: (item: any) => {
        this.signOut();
      }
    },
  ];

  activeCustomer$: Observable<Customer>;
  private unsubscribe$ = new Subject<void>();

  public currentAddress$: Observable<Address | undefined>;

  public ordersCount$: Observable<number | undefined>;
  constructor(public router: Router, private route: ActivatedRoute, private stateService: StateService,
    private dataService: DataService, private platform: Platform, private activeService: ActiveService,
    private vehicleTypeService: VehicleTypeService,
    private savedItemsService: SavedItemsService,
    public selectedModificationService: SelectedModificationService,
    private locationService: Location
  ) {

  }



  ngOnInit(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateActiveStatus();
      }
    });

    if (this.platform.isBrowser) {
      this.getActiveCustomer();
    }
    this.getOrders();
    this.updateActiveStatus();
    this.subscribeToSavedItemsCount();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private subscribeToSavedItemsCount() {
    this.savedItemsService.savedItemsCount$.pipe(takeUntil(this.unsubscribe$)).subscribe(count => {
      this.profileMenuList[1].count = count;
    })
  }

  private getOrders() {
    this.ordersCount$ = this.dataService.query<GetOrderListQuery, GetOrderListQueryVariables>(GET_ORDER_COUNT, {
      options: {
        filter: {
          active: {
            eq: false,
          },
        },
      },
    }).pipe(
      map(data => data.activeCustomer && data.activeCustomer.orders?.totalItems as number),
      takeUntil(this.unsubscribe$)
    );

    this.ordersCount$.subscribe({
      next: (response: number | undefined) => {
        if (response) {
          this.profileMenuList[0].count = response;
        }
      }
    })
  }

  private getActiveCustomer() {
    this.activeCustomer$ = this.activeService.activeCustomer$.pipe(takeUntil(this.unsubscribe$))

    this.currentAddress$ = this.activeCustomer$.pipe(
      map((data: Customer) => data.addresses?.find(address => address.defaultShippingAddress))
    )

  }


  private fetchActiveCustomer(): Observable<Customer> {
    return this.dataService.query<GetActiveCustomerQuery>(GET_USER_MENU_ACTIVE_CUSTOMER, {}, 'network-only').pipe(
      map(data => data.activeCustomer as Customer),
    );
  }

  signOut() {
    this.dataService.mutate<SignOutMutation>(SIGN_OUT).pipe(take(1)).subscribe({
      next: () => {
        this.activeService.suggestedVehicleDialogIsOpened = true;
        this.selectedModificationService.selectedModificationId = null;
        this.stateService.setState('selectedModificationId', null);
        this.stateService.setState('activeVehicleId', null);
        this.stateService.setState('signedIn', false);

        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate(['/'+(this.vehicleTypeService.currentVehicleType)],
            {
              queryParams: { modificationId: null }
            }
          )
        });

        return;

      },
    });
  }

  public triggerEventsAfterLinkClick(item?: ProfileMenuLinkItem) {
    if (this.inMobileSidenav) {
      this.stateService.setState('userMobileMenuVisible', false);
    }
    if (item?.command) {
      item.command(item);
    }
  }
  updateActiveStatus(): void {
    const currentUrl = this.router.url;

    this.profileMenuList.forEach(item => {
      if (item.routerLink) {
        item.active = currentUrl.includes(item.routerLink);
        if (item.children) {
          item.children.forEach(child => {
            if (child.routerLink) {
              child.active = currentUrl.includes(child.routerLink);
            }
            if (child.active) {
              item.active = true;
            }
          })
        }
      }
    });
  }

}
